import { Matrix, matrixAxisLabel } from "~/components/design-system/components"
import { useCompany } from "~/hooks/useCompany"
import type { MatrixBlockData, MatrixValueFormat } from "~/server/api/routers/haki/response/blocks"
import { getNumberFormatLocale } from "~/stores/number-format-store"
import { formatNumber } from "~/utils/formatting"

interface ChatMatrixBlockProps {
  validated?: unknown
  /** Drop the title; the surrounding tile shows its own name. */
  bare?: boolean
  compact?: boolean
}

type CellFormatter = (value: number, total: number) => string

// One fixed decimal, so 9.0 and 22.4 line up in a column instead of reading "9" and "22,4".
const oneDecimal = (value: number) =>
  value.toLocaleString(getNumberFormatLocale(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

const CELL_FORMAT: Record<MatrixValueFormat, CellFormatter> = {
  number: (value) => formatNumber(value, 0),
  currency: (value) => formatNumber(value, 0),
  decimal: oneDecimal,
  percent: oneDecimal,
  share: (value, total) => oneDecimal(total > 0 ? (value / total) * 100 : 0),
}

/** The unit is stated once in the footer after the measure, never per cell (MATRIX_INTENT). */
export function matrixUnit(data: MatrixBlockData, currency: string): string | undefined {
  if (data.unit) return data.unit
  if (data.format === "currency") return currency
  if (data.format === "percent" || data.format === "share") return "%"
  return undefined
}

export function isMatrixData(data: unknown): data is MatrixBlockData {
  const record = data as Partial<MatrixBlockData> | null | undefined
  return (
    Array.isArray(record?.rowHeaders) &&
    Array.isArray(record.columnHeaders) &&
    Array.isArray(record.values)
  )
}

/** "Category × Month" for the card meta, left out when the title already names both axes (MATRIX_INTENT). */
export function matrixTitleMeta(title: string | undefined, data: MatrixBlockData) {
  const meta = matrixAxisLabel(data.rowAxisLabel, data.columnAxisLabel)
  if (!meta || !title) return meta
  const lower = title.toLowerCase()
  const named = [data.rowAxisLabel, data.columnAxisLabel].every((axis) =>
    lower.includes(axis!.toLowerCase()),
  )
  return named ? undefined : meta
}

export default function ChatMatrixBlock({ validated, bare, compact }: ChatMatrixBlockProps) {
  const { data: company } = useCompany()

  if (!isMatrixData(validated)) {
    return (
      <pre className="overflow-x-auto rounded-lg bg-medium p-3 text-xs">
        <code>Invalid matrix block</code>
      </pre>
    )
  }

  const title = validated.title ?? validated.shortTitle
  const meta = bare ? undefined : matrixTitleMeta(title, validated)

  return (
    <div className={bare ? undefined : "my-2 flex flex-col gap-2"}>
      {!bare && (title || meta) && (
        <div className="flex min-w-0 items-baseline gap-2">
          {title && <span className="label-base min-w-0 truncate">{title}</span>}
          {meta && <span className="paragraph-sm text-fg-muted shrink-0">{meta}</span>}
        </div>
      )}
      <Matrix
        aria-label={title ?? "Matrix"}
        rowHeaders={validated.rowHeaders}
        columnHeaders={validated.columnHeaders}
        values={validated.values}
        formatValue={CELL_FORMAT[validated.format] ?? CELL_FORMAT.number}
        heat={validated.heat}
        heatColor={validated.heatColor}
        totals={validated.totals}
        measure={validated.measure}
        unit={matrixUnit(validated, company?.currency ?? "EUR")}
        rowAxisLabel={validated.rowAxisLabel}
        columnAxisLabel={validated.columnAxisLabel}
        density={compact ? "compact" : "base"}
      />
    </div>
  )
}
